import type { Amount, Order, OrderContractElement, PaymentTerms } from '@hero/schema';

/**
 * Phase 7 (relations-v2) — per-leg projection helpers.
 *
 * The deprecated `Order.payment.{beneficiary,carrier}` triple has been
 * removed; the single source of truth for commercial state is now
 * `Order.contracts[i]`. These helpers replace the most common reader
 * patterns:
 *
 *   - `order.payment.beneficiary.amount` → {@link getCustomerAmount}
 *   - `order.payment.carrier.amount`       → {@link getCarrierAmount}
 *   - `order.payment.beneficiary.payment.terms` → {@link getCustomerPaymentTerms}
 *   - `order.payment.beneficiary.payment.invoices` → {@link getCustomerInvoices}
 *   - `order.payment.beneficiary.payment.transactions` → {@link getCustomerTransactions}
 *
 * For a direct (broker-less) order with one leg, customer and carrier
 * helpers return the same leg. For chained orders the customer leg is
 * `contracts[0]` (where the customer is the buyer) and the carrier
 * leg is `contracts[contracts.length - 1]` (where the executor is
 * the seller).
 */

/** Buyer-most leg — where the customer is the buyer. `undefined` for orders with no contracts. */
export const getCustomerLeg = (order: Order): OrderContractElement | undefined => {
  const contracts = order.contracts ?? [];
  return contracts[0];
};

/** Seller-most leg — where the executing carrier is the seller. */
export const getCarrierLeg = (order: Order): OrderContractElement | undefined => {
  const contracts = order.contracts ?? [];
  if (contracts.length === 0) return undefined;
  return contracts[contracts.length - 1];
};

/**
 * A neutral-element {@link Amount} used by readers that expect a
 * non-nullable amount block on every order (e.g. analytics summaries
 * that aggregate across thousands of orders and treat "no contract"
 * as zero contribution).
 */
export const ZERO_ORDER_AMOUNT: Amount = {
  currency: 'SAR',
  total: 0,
  paid: 0,
  remaining: 0,
  subtotal: 0,
  itemsSubtotal: 0,
  realizedSubtotal: 0,
  itemsOriginalSubtotal: 0,
  tax: { total: 0, applied: [] },
  fees: { added: 0, subtracted: 0, realized: 0, all: 0, total: 0, applied: [] },
  discount: { amount: 0, percentage: 0 },
};

export const getCustomerAmount = (order: Order): Amount =>
  getCustomerLeg(order)?.amount ?? ZERO_ORDER_AMOUNT;

export const getCarrierAmount = (order: Order): Amount =>
  getCarrierLeg(order)?.amount ?? ZERO_ORDER_AMOUNT;

export const getCustomerPaymentTerms = (order: Order): PaymentTerms | undefined =>
  getCustomerLeg(order)?.payment.terms;

export const getCustomerPaymentStatus = (order: Order) =>
  getCustomerLeg(order)?.payment.status;

export const getCustomerInvoices = (order: Order) => getCustomerLeg(order)?.invoices ?? [];

export const getCustomerTransactions = (order: Order) =>
  getCustomerLeg(order)?.transactions ?? [];

export const getCarrierInvoices = (order: Order) => getCarrierLeg(order)?.invoices ?? [];

export const getCarrierTransactions = (order: Order) =>
  getCarrierLeg(order)?.transactions ?? [];

/** Derived margin: customer total minus carrier total (replaces `payment.commission.amount`). */
export const getOrderMarginTotal = (order: Order): number =>
  getCustomerAmount(order).total - getCarrierAmount(order).total;
